import { TableRow, TableCell, Button, Tooltip, Box } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useAppContext } from '../context/AppContext';
import { formatWeight } from '../utils/formatNumber';
import { parseBoolean } from '../utils/boolean';
import { use, useMemo } from 'react';

const RecipeIngredientRow = ({
  idx,
  ing,
  ingredientOptions,
  handleIngredientChange,
  removeIngredient,
  disableDelete,
  visibleColumns = {},
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
  mode = 'mix'
}) => {
  const { t } = useTranslation();
  const { language } = useAppContext();
  const ingData = ingredientOptions.find(opt => opt.id === ing.id);
  const canBeMix = ingData ? ingData.can_be_mix !== false : true;
  const canBeInclusion = ingData ? ingData.can_be_inclusion === true : true;
  const rawInMix = ing.in_mix;
  let inMix = parseBoolean(rawInMix, true);

  const isMixRow = mode === 'mix';

  const qty = Number(ing.amount) || 0;
  const fat = ingData ? (qty * (ingData.fats || 0) / 100) : 0;
  const msnf = ingData ? (qty * (ingData.msnf || 0) / 100) : 0;
  const solids = ingData ? (qty * (ingData.total_solids || 0) / 100) : 0;
  const pac = ingData ? (qty * (ingData.pac || 0) / 100) : 0;
  const pod = ingData ? (qty * (ingData.pod || 0) / 100) : 0;
  const sugars = ingData ? (qty * (ingData.sugars || 0) / 100) : 0;
  const lactose = ingData ? (qty * (ingData.lactose || 0) / 100) : 0;
  const water = ingData ? (qty * (ingData.water || 0) / 100) : 0;
  const proteins = ingData ? (qty * (ingData.proteins || 0) / 100) : 0;
  const alcohol = ingData ? (qty * (ingData.alcohol || 0) / 100) : 0;

  const formatted = (value) => (inMix ? formatWeight(value) : '—');

  const ingredientValidOptions = useMemo(() => {
    return ingredientOptions.filter(opt => {
      if (isMixRow) {
        return opt.can_be_mix !== false;
      } else {
        return opt.can_be_inclusion === true;
      }
    });
  }, [ingredientOptions, isMixRow]);

  return (
    <TableRow
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={{ cursor: 'move' }}
      sx={{ 
        '&:last-child td, &:last-child th': { border: 0 },
        ...(ingData?._isTemporary && { 
          backgroundColor: '#fff3e0', 
          '& .MuiAutocomplete-root': { 
            '& .MuiOutlinedInput-root': { 
              backgroundColor: '#fff3e0' 
            } 
          }
        })
      }}
    >
      <TableCell width={16}>
        <DragIndicatorIcon sx={{ color: '#888', cursor: 'grab' }} />
      </TableCell>
      <TableCell component="th" scope="row" align="left" sx={{ minWidth: 240, maxWidth: 300, width: 240 }}>
        <Autocomplete
          options={ingredientValidOptions}
          size="small"
          getOptionLabel={opt => {
            const label = opt.translations?.[language] || opt.name || '';
            return opt._isTemporary ? `${label} (${t('unavailable') || 'no disponible'})` : label;
          }}
          value={ingredientValidOptions.find(opt => opt.id === ing.id) || null}
          onChange={(_, newValue) => handleIngredientChange(idx, 'id', newValue ? newValue.id : '')}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={params => (
            <TextField
              {...params}
              label={''}
              fullWidth
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <span style={{ 
                color: option._isTemporary ? '#f57c00' : 'inherit',
                fontStyle: option._isTemporary ? 'italic' : 'normal'
              }}>
                {option.translations?.[language] || option.name || ''}
                {option._isTemporary && ` (${t('unavailable') || 'no disponible'})`}
              </span>
            </li>
          )}
        />
      </TableCell>
      <TableCell align="left" sx={{ minWidth: 60, maxWidth: 100, width: 100 }}>
        <TextField
          label={''}
          type="number"
          value={ing.amount}
          onChange={e => handleIngredientChange(idx, 'amount', e.target.value)}
          fullWidth
          size="small"
          inputProps={{ min: 0, step: "any" }}
        />
      </TableCell>
      {visibleColumns.fat && <TableCell align="center">{formatted(fat)}</TableCell>}
      {visibleColumns.msnf && <TableCell align="center">{formatted(msnf)}</TableCell>}
      {visibleColumns.total_solids && <TableCell align="center">{formatted(solids)}</TableCell>}
      {visibleColumns.pac && <TableCell align="center">{formatted(pac)}</TableCell>}
      {visibleColumns.pod && <TableCell align="center">{formatted(pod)}</TableCell>}
      {visibleColumns.sugars && <TableCell align="center">{formatted(sugars)}</TableCell>}
      {visibleColumns.lactose && <TableCell align="center">{formatted(lactose)}</TableCell>}
      {visibleColumns.water && <TableCell align="center">{formatted(water)}</TableCell>}
      {visibleColumns.proteins && <TableCell align="center">{formatted(proteins)}</TableCell>}
      {visibleColumns.alcohol && <TableCell align="center">{formatted(alcohol)}</TableCell>}
      <TableCell sx={{ width: 24, textAlign: 'right' }}>
    
        <Button
          size="small"
          color="error"
          onClick={() => removeIngredient(idx)}
          disabled={disableDelete}
        >
          ×
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default RecipeIngredientRow;